'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import SectionTitle from '@/components/ui/SectionTitle';

import { FileText, Plus, Search, Eye, Edit2, Download, X, Calendar, DollarSign, MapPin, User, Building2, CheckCircle2, Clock, AlertCircle, XCircle, ChevronDown, ChevronUp,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
type ContractStatus = 'active' | 'pending' | 'completed' | 'terminated' | 'draft';

interface Contract {
  id: string;
  title: string;
  contractor: string;
  company: string;
  region: string;
  country: string;
  value: number;
  currency: string;
  startDate: string;
  endDate: string;
  status: ContractStatus;
  scope: string;
  deliverables: string[];
  completionPct: number;
  contactPerson: string;
  contactEmail: string;
  issueDate: string;
  lastUpdated: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const mockContracts: Contract[] = [
  {
    id: 'CTR-2024-001',
    title: 'Rural Electrification – Northern Corridor',
    contractor: 'Volta Power Solutions Ltd',
    company: 'WAEP Ghana',
    region: 'Northern Region',
    country: 'Ghana',
    value: 1_250_000,
    currency: 'USD',
    startDate: '2024-01-15',
    endDate: '2024-12-31',
    status: 'active',
    scope: 'Installation of 45km medium-voltage distribution lines, 12 transformers, and 320 service connections across 8 communities in the Northern Corridor.',
    deliverables: ['MV Line Installation', 'Transformer Mounting', 'Service Connections', 'Commissioning Report'],
    completionPct: 68,
    contactPerson: 'Kwame Asante',
    contactEmail: 'k.asante@voltapower.gh',
    issueDate: '2024-01-10',
    lastUpdated: '2024-07-18',
  },
  {
    id: 'CTR-2024-002',
    title: 'Streetlight Rehabilitation – Accra Metro',
    contractor: 'BrightPath Engineering',
    company: 'WAEP Ghana',
    region: 'Greater Accra',
    country: 'Ghana',
    value: 480_000,
    currency: 'USD',
    startDate: '2024-03-01',
    endDate: '2024-08-31',
    status: 'active',
    scope: 'Replacement and upgrade of 1,200 streetlights with energy-efficient LED fixtures along 18 major corridors in Accra Metropolitan Area.',
    deliverables: ['LED Fixture Supply', 'Installation Works', 'Control System Setup', 'Handover Documentation'],
    completionPct: 82,
    contactPerson: 'Abena Mensah',
    contactEmail: 'a.mensah@brightpath.gh',
    issueDate: '2024-02-20',
    lastUpdated: '2024-07-20',
  },
  {
    id: 'CTR-2024-003',
    title: 'Substation Upgrade – Abidjan South',
    contractor: 'Électricité Côte d\'Ivoire SARL',
    company: 'WAEP Côte d\'Ivoire',
    region: 'Abidjan District',
    country: 'Côte d\'Ivoire',
    value: 2_100_000,
    currency: 'USD',
    startDate: '2024-02-01',
    endDate: '2025-01-31',
    status: 'active',
    scope: 'Full upgrade of the Abidjan South 33/11kV substation including new switchgear, protection systems, SCADA integration, and civil works.',
    deliverables: ['Civil Works', 'Switchgear Installation', 'SCADA Integration', 'Testing & Commissioning'],
    completionPct: 45,
    contactPerson: 'Jean-Pierre Kouassi',
    contactEmail: 'jp.kouassi@eci-sarl.ci',
    issueDate: '2024-01-25',
    lastUpdated: '2024-07-15',
  },
  {
    id: 'CTR-2024-004',
    title: 'Last-Mile Connections – Dakar Peri-Urban',
    contractor: 'SénéElec Contractors',
    company: 'WAEP Senegal',
    region: 'Dakar Region',
    country: 'Senegal',
    value: 670_000,
    currency: 'USD',
    startDate: '2024-05-01',
    endDate: '2024-11-30',
    status: 'pending',
    scope: 'Provision of last-mile electricity connections to 850 households in 6 peri-urban communities around Dakar, including metering and billing setup.',
    deliverables: ['Service Cable Installation', 'Meter Provision', 'Billing System Setup', 'Community Handover'],
    completionPct: 0,
    contactPerson: 'Fatou Diallo',
    contactEmail: 'f.diallo@seneelec.sn',
    issueDate: '2024-04-22',
    lastUpdated: '2024-07-01',
  },
  {
    id: 'CTR-2023-018',
    title: 'Transformer Supply & Installation – Kumasi',
    contractor: 'PowerGrid West Africa',
    company: 'WAEP Ghana',
    region: 'Ashanti Region',
    country: 'Ghana',
    value: 390_000,
    currency: 'USD',
    startDate: '2023-06-01',
    endDate: '2023-12-31',
    status: 'completed',
    scope: 'Supply and installation of 28 distribution transformers (100kVA–500kVA) across Kumasi Metropolitan Area and surrounding districts.',
    deliverables: ['Transformer Supply', 'Civil Plinths', 'HV/LV Connections', 'Test Certificates'],
    completionPct: 100,
    contactPerson: 'Kofi Boateng',
    contactEmail: 'k.boateng@powergridwa.com',
    issueDate: '2023-05-28',
    lastUpdated: '2024-01-10',
  },
  {
    id: 'CTR-2023-021',
    title: 'Metering Infrastructure – Lomé Central',
    contractor: 'TogoTech Solutions',
    company: 'WAEP Togo',
    region: 'Maritime Region',
    country: 'Togo',
    value: 215_000,
    currency: 'USD',
    startDate: '2023-09-01',
    endDate: '2024-02-28',
    status: 'completed',
    scope: 'Installation of 2,400 smart prepayment meters in Lomé Central district with AMI communication infrastructure.',
    deliverables: ['Smart Meter Supply', 'AMI Network Setup', 'Customer Enrollment', 'Training'],
    completionPct: 100,
    contactPerson: 'Ama Koffi',
    contactEmail: 'a.koffi@togatech.tg',
    issueDate: '2023-08-20',
    lastUpdated: '2024-03-05',
  },
  {
    id: 'CTR-2024-005',
    title: 'Grid Extension – Bamako Outskirts',
    contractor: 'Mali Énergie Contractors',
    company: 'WAEP Mali',
    region: 'Bamako District',
    country: 'Mali',
    value: 880_000,
    currency: 'USD',
    startDate: '2024-04-01',
    endDate: '2025-03-31',
    status: 'draft',
    scope: 'Extension of the medium-voltage grid to 12 unelectrified communities on the outskirts of Bamako, covering approximately 60km of new MV lines.',
    deliverables: ['Route Survey', 'MV Line Construction', 'LV Distribution', 'Connections & Metering'],
    completionPct: 0,
    contactPerson: 'Ibrahim Traoré',
    contactEmail: 'i.traore@malienergiec.ml',
    issueDate: '2024-03-28',
    lastUpdated: '2024-07-22',
  },
  {
    id: 'CTR-2023-015',
    title: 'Solar Hybrid Mini-Grid – Tamale',
    contractor: 'SunPower Africa Ltd',
    company: 'WAEP Ghana',
    region: 'Northern Region',
    country: 'Ghana',
    value: 1_750_000,
    currency: 'USD',
    startDate: '2023-03-01',
    endDate: '2023-11-30',
    status: 'terminated',
    scope: 'Design and construction of a 500kWp solar hybrid mini-grid with battery storage for 3 communities near Tamale.',
    deliverables: ['Solar Array', 'Battery Storage', 'Distribution Network', 'O&M Training'],
    completionPct: 34,
    contactPerson: 'Nana Yaw Owusu',
    contactEmail: 'n.owusu@sunpowerafrica.com',
    issueDate: '2023-02-15',
    lastUpdated: '2023-09-12',
  },
];

// ─── Status Config ─────────────────────────────────────────────────────────────
const statusConfig: Record<ContractStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  active:     { label: 'Active',     color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   icon: CheckCircle2 },
  pending:    { label: 'Pending',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: Clock },
  completed:  { label: 'Completed',  color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', icon: CheckCircle2 },
  terminated: { label: 'Terminated', color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  icon: XCircle },
  draft:      { label: 'Draft',      color: '#94a3b8', bg: 'rgba(148,163,184,0.12)',icon: AlertCircle },
};

const ALL_STATUSES: ContractStatus[] = ['active', 'pending', 'completed', 'terminated', 'draft'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatCurrency(value: number, currency: string) {
  return `${currency} ${value.toLocaleString('en-US')}`;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Issue Contract Modal ─────────────────────────────────────────────────────
interface IssueModalProps {
  onClose: () => void;
  onSubmit: (data: Partial<Contract>) => void;
}

function IssueContractModal({ onClose, onSubmit }: IssueModalProps) {
  const [form, setForm] = useState({
    title: '', contractor: '', company: '', region: '', country: '',
    value: '', currency: 'USD', startDate: '', endDate: '', scope: '',
  });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...form, value: parseFloat(form.value) || 0, status: 'draft' as ContractStatus });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--surface)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}>
              <FileText size={16} style={{ color: 'var(--accent)' }} />
            </div>
            <h2 className="text-base font-semibold text-white">Issue New Contract</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Contract Title *</label>
              <input
                required value={form.title} onChange={e => set('title', e.target.value)}
                placeholder="e.g. Rural Electrification – Northern Corridor"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none focus:ring-1"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', focusRingColor: 'var(--accent)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Contractor *</label>
              <input
                required value={form.contractor} onChange={e => set('contractor', e.target.value)}
                placeholder="Contractor company name"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Issuing Company</label>
              <input
                value={form.company} onChange={e => set('company', e.target.value)}
                placeholder="e.g. WAEP Ghana"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Region</label>
              <input
                value={form.region} onChange={e => set('region', e.target.value)}
                placeholder="e.g. Northern Region"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Country</label>
              <input
                value={form.country} onChange={e => set('country', e.target.value)}
                placeholder="e.g. Ghana"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Contract Value *</label>
              <input
                required type="number" min="0" value={form.value} onChange={e => set('value', e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Currency</label>
              <select
                value={form.currency} onChange={e => set('currency', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <option value="USD">USD</option>
                <option value="GHS">GHS</option>
                <option value="XOF">XOF</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Start Date *</label>
              <input
                required type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">End Date *</label>
              <input
                required type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Scope of Work</label>
              <textarea
                rows={3} value={form.scope} onChange={e => set('scope', e.target.value)}
                placeholder="Describe the scope of work, deliverables, and objectives..."
                className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none resize-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              Issue Contract
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Contract Detail Modal ────────────────────────────────────────────────────
interface DetailModalProps {
  contract: Contract;
  onClose: () => void;
}

function ContractDetailModal({ contract, onClose }: DetailModalProps) {
  const cfg = statusConfig[contract.status];
  const StatusIcon = cfg.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--surface)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-start justify-between gap-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{contract.id}</span>
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ color: cfg.color, backgroundColor: cfg.bg }}
              >
                <StatusIcon size={10} />
                {cfg.label}
              </span>
            </div>
            <h2 className="text-base font-semibold text-white leading-snug">{contract.title}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-5">
          {/* Key Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Building2, label: 'Contractor', value: contract.contractor },
              { icon: Building2, label: 'Issuing Company', value: contract.company },
              { icon: MapPin, label: 'Region', value: `${contract.region}, ${contract.country}` },
              { icon: DollarSign, label: 'Contract Value', value: formatCurrency(contract.value, contract.currency) },
              { icon: Calendar, label: 'Start Date', value: formatDate(contract.startDate) },
              { icon: Calendar, label: 'End Date', value: formatDate(contract.endDate) },
              { icon: User, label: 'Contact Person', value: contract.contactPerson },
              { icon: Calendar, label: 'Issued On', value: formatDate(contract.issueDate) },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={12} className="text-gray-500" />
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
                </div>
                <p className="text-sm font-medium text-white truncate">{value}</p>
              </div>
            ))}
          </div>

          {/* Progress */}
          {contract.status !== 'draft' && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-400">Completion Progress</span>
                <span className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{contract.completionPct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${contract.completionPct}%`, backgroundColor: cfg.color }}
                />
              </div>
            </div>
          )}

          {/* Scope */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Scope of Work</p>
            <p className="text-sm text-gray-300 leading-relaxed">{contract.scope}</p>
          </div>

          {/* Deliverables */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">Key Deliverables</p>
            <div className="flex flex-wrap gap-2">
              {contract.deliverables.map((d) => (
                <span
                  key={d}
                  className="text-xs px-2.5 py-1 rounded-full font-medium"
                  style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: 'var(--accent)' }}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 flex items-center justify-between gap-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <span className="text-xs text-gray-500">Last updated: {formatDate(contract.lastUpdated)}</span>
          <div className="flex gap-2">
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <Download size={13} /> Export PDF
            </button>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              <Edit2 size={13} /> Edit Contract
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Contract Card ────────────────────────────────────────────────────────────
interface ContractCardProps {
  contract: Contract;
  onView: (c: Contract) => void;
}

function ContractCard({ contract, onView }: ContractCardProps) {
  const cfg = statusConfig[contract.status];
  const StatusIcon = cfg.icon;
  const daysLeft = Math.ceil((new Date(contract.endDate).getTime() - Date.now()) / 86_400_000);

  return (
    <div
      className="rounded-xl p-4 transition-all duration-200 hover:scale-[1.01] cursor-pointer group"
      style={{
        backgroundColor: 'rgba(18, 22, 36, 0.97)',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.06) inset',
      }}
      onClick={() => onView(contract)}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[11px] font-mono font-bold" style={{ color: 'var(--accent)' }}>{contract.id}</span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ color: cfg.color, backgroundColor: cfg.bg }}
            >
              <StatusIcon size={9} />
              {cfg.label}
            </span>
          </div>
          <h3 className="text-sm font-bold text-white leading-snug group-hover:text-amber-300 transition-colors line-clamp-2">
            {contract.title}
          </h3>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onView(contract); }}
          className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all flex-shrink-0"
        >
          <Eye size={14} className="text-gray-300" />
        </button>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <Building2 size={12} className="text-amber-400 flex-shrink-0" />
          <span className="text-xs text-gray-100 truncate font-medium">{contract.contractor}</span>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <MapPin size={12} className="text-blue-400 flex-shrink-0" />
          <span className="text-xs text-gray-100 truncate font-medium">{contract.region}</span>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <DollarSign size={12} className="text-green-400 flex-shrink-0" />
          <span className="text-xs text-gray-100 truncate font-medium">{formatCurrency(contract.value, contract.currency)}</span>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <Calendar size={12} className="text-purple-400 flex-shrink-0" />
          <span className="text-xs text-gray-100 truncate font-medium">
            {contract.status === 'completed' ? 'Completed' : contract.status === 'terminated' ? 'Terminated' : daysLeft > 0 ? `${daysLeft}d left` : 'Overdue'}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      {contract.status !== 'draft' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-semibold text-gray-300">Progress</span>
            <span className="text-[10px] font-bold" style={{ color: cfg.color }}>{contract.completionPct}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${contract.completionPct}%`, backgroundColor: cfg.color, boxShadow: `0 0 6px ${cfg.color}80` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KPI Summary ──────────────────────────────────────────────────────────────
function ContractKpis({ contracts }: { contracts: Contract[] }) {
  const totalValue = contracts.reduce((s, c) => s + c.value, 0);
  const active = contracts.filter(c => c.status === 'active').length;
  const pending = contracts.filter(c => c.status === 'pending').length;
  const completed = contracts.filter(c => c.status === 'completed').length;

  const kpis = [
    { label: 'Total Contracts', value: contracts.length, sub: 'All time', color: '#94a3b8' },
    { label: 'Active', value: active, sub: 'Currently running', color: '#22c55e' },
    { label: 'Pending', value: pending, sub: 'Awaiting start', color: '#f59e0b' },
    { label: 'Completed', value: completed, sub: 'Successfully closed', color: '#3b82f6' },
    { label: 'Total Value', value: `$${(totalValue / 1_000_000).toFixed(2)}M`, sub: 'USD equivalent', color: 'var(--accent)' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {kpis.map(({ label, value, sub, color }) => (
        <div
          key={label}
          className="rounded-xl p-4"
          style={{
            backgroundColor: 'rgba(18, 22, 36, 0.97)',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          }}
        >
          <p className="text-[11px] font-semibold text-gray-400 mb-1">{label}</p>
          <p className="text-2xl font-bold" style={{ color }}>{value}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ContractsPage() {
  const [contracts, setContracts] = useState<Contract[]>(mockContracts);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'all'>('all');
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [sortField, setSortField] = useState<'value' | 'startDate' | 'endDate'>('startDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    let list = contracts.filter(c => {
      const matchSearch =
        !search ||
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        c.contractor.toLowerCase().includes(search.toLowerCase()) ||
        c.region.toLowerCase().includes(search.toLowerCase()) ||
        c.id.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || c.status === statusFilter;
      return matchSearch && matchStatus;
    });

    list = [...list].sort((a, b) => {
      let av: number, bv: number;
      if (sortField === 'value') { av = a.value; bv = b.value; }
      else { av = new Date(a[sortField]).getTime(); bv = new Date(b[sortField]).getTime(); }
      return sortDir === 'asc' ? av - bv : bv - av;
    });

    return list;
  }, [contracts, search, statusFilter, sortField, sortDir]);

  const handleIssue = (data: Partial<Contract>) => {
    const newContract: Contract = {
      id: `CTR-${new Date().getFullYear()}-${String(contracts.length + 1).padStart(3, '0')}`,
      title: data.title || '',
      contractor: data.contractor || '',
      company: data.company || '',
      region: data.region || '',
      country: data.country || '',
      value: data.value || 0,
      currency: data.currency || 'USD',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      status: 'draft',
      scope: data.scope || '',
      deliverables: [],
      completionPct: 0,
      contactPerson: '',
      contactEmail: '',
      issueDate: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0],
    };
    setContracts(prev => [newContract, ...prev]);
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  return (
    <AppLayout>
      <div
        className="p-6 min-h-screen"
        style={{
          background: 'radial-gradient(ellipse at 20% 10%, rgba(245,158,11,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(59,130,246,0.07) 0%, transparent 50%), var(--bg)',
        }}
      >
        {/* Page Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <SectionTitle
            title="Contracts Management"
            subtitle="Issue, manage, and track contracts between the company and contractors"
          />
          <button
            onClick={() => setShowIssueModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 flex-shrink-0"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <Plus size={16} />
            Issue Contract
          </button>
        </div>

        {/* KPI Row */}
        <ContractKpis contracts={contracts} />

        {/* Filters Bar */}
        <div
          className="flex flex-wrap items-center gap-3 mb-5 p-3 rounded-xl"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search contracts, contractors, regions..."
              className="w-full pl-8 pr-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 outline-none"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
            />
          </div>

          {/* Status Pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === 'all' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={statusFilter === 'all' ? { backgroundColor: 'rgba(255,255,255,0.12)' } : {}}
            >
              All
            </button>
            {ALL_STATUSES.map(s => {
              const c = statusConfig[s];
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={statusFilter === s
                    ? { backgroundColor: c.bg, color: c.color }
                    : { color: '#6b7280' }
                  }
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs text-gray-500">Sort:</span>
            {(['value', 'startDate', 'endDate'] as const).map(f => (
              <button
                key={f}
                onClick={() => toggleSort(f)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={sortField === f ? { backgroundColor: 'rgba(245,158,11,0.15)', color: 'var(--accent)' } : { color: '#6b7280' }}
              >
                {f === 'value' ? 'Value' : f === 'startDate' ? 'Start' : 'End'}
                {sortField === f && (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <p className="text-xs text-gray-500 mb-4">
          Showing <span className="text-white font-medium">{filtered.length}</span> of {contracts.length} contracts
        </p>

        {/* Contract Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(c => (
              <ContractCard key={c.id} contract={c} onView={setSelectedContract} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <FileText size={40} className="text-gray-600 mb-3" />
            <p className="text-gray-400 font-medium">No contracts found</p>
            <p className="text-gray-600 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showIssueModal && (
        <IssueContractModal onClose={() => setShowIssueModal(false)} onSubmit={handleIssue} />
      )}
      {selectedContract && (
        <ContractDetailModal contract={selectedContract} onClose={() => setSelectedContract(null)} />
      )}
    </AppLayout>
  );
}
